import { useDisableBodyPointerEvents } from '#app/components/Modals/hooks/useDisableBodyPointerEvents.js';
import { usePreventLayoutShifting } from '#app/hooks/usePreventLayoutShifting.js';
import { type ModalBaseProps } from '../types.js';
import { ModalActions } from './ModalActions.js';
import { ModalBody } from './ModalBody.js';
import { ModalCloseButton } from './ModalCloseButton.js';
import { ModalDialog } from './ModalDialog.js';
import { ModalHeader } from './ModalHeader.js';
import { ModalHero } from './ModalHero.js';
import { ModalProgress } from './ModalProgress.js';
import { ModalSubheader } from './ModalSubheader.js';
import { ModalTitle } from './ModalTitle.js';
import { usePreventScroll } from '@react-aria/overlays';
import { AnimatePresence } from 'framer-motion';

type ModalProps = ModalBaseProps & {
  readonly ariaLabel: string;
  readonly lockPointerEvents?: boolean;
};

const ModalV2 = ({
  ariaLabel,
  blurBackdrop = false,
  children,
  customBackdropOpacity,
  customBelowModalContent,
  customZIndex,
  isDismissible = false,
  isKeyboardDismissDisabled = false,
  isOpen = false,
  lockPointerEvents = false,
  onClose,
  overflowX,
  overrideBackgroundColor,
  overrideBackgroundImageUrl,
  overrideBoxShadow,
  role = 'dialog',
  shouldCloseOnBlur,
  shouldCloseOnInteractOutside,
  size = 'md',
  ...props
}: ModalProps) => {
  usePreventScroll({ isDisabled: !isOpen });
  usePreventLayoutShifting(isOpen);
  useDisableBodyPointerEvents({
    disabled: isOpen && lockPointerEvents,
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalDialog
          ariaLabel={ariaLabel}
          blurBackdrop={blurBackdrop}
          customBackdropOpacity={customBackdropOpacity}
          customBelowModalContent={customBelowModalContent}
          customZIndex={customZIndex}
          isDismissible={isDismissible}
          isKeyboardDismissDisabled={isKeyboardDismissDisabled}
          isOpen={isOpen}
          onClose={onClose}
          overflowX={overflowX}
          overrideBackgroundColor={overrideBackgroundColor}
          overrideBackgroundImageUrl={overrideBackgroundImageUrl}
          overrideBoxShadow={overrideBoxShadow}
          role={role}
          shouldCloseOnBlur={shouldCloseOnBlur}
          shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
          size={size}
          {...props}
        >
          {children}
        </ModalDialog>
      )}
    </AnimatePresence>
  );
};

// Subcomponents
ModalV2.Body = ModalBody;
ModalV2.Header = ModalHeader;
ModalV2.Title = ModalTitle;
ModalV2.Close = ModalCloseButton;
ModalV2.Subheader = ModalSubheader;
ModalV2.Actions = ModalActions;
ModalV2.Hero = ModalHero;
ModalV2.Progress = ModalProgress;

export { ModalV2 };
